/*
 * HVACloud AI · Dashboard-Seiten vorschlagen — Task modal.
 *
 * POST /api/plants/{p}/suggest-dashboard → Vorschlag mit Seiten + bereits
 * assembliertem body_json. Übernehmen speichert es als Personal-Layout via
 * PUT /api/layouts/plant/{p}; die Optimistic-Concurrency-Version holen wir
 * unmittelbar vorher, damit wir nicht mit einer stale Version stehen bleiben.
 */

import { useState } from 'preact/hooks';
import { AiTaskChrome, type AiTaskStage } from './AiTaskChrome';
import { api, ApiError } from '../../api/client';
import type { LayoutDetailResponse } from '../../api/types';
import { AiDashboard } from '../../icons/HvacIcons';
import { BRAND } from '../../brand/brand';

interface Props {
  plantId: string;
  onClose(): void;
}

interface ProposalWidget {
  typeId: string;
  title: string | null;
  leafSymbols: string[];
}
interface ProposalPage {
  title: string;
  widgets: ProposalWidget[];
}
interface DashboardProposal {
  plantId: string;
  pages: ProposalPage[];
  bodyJson: string;
  rationale: string | null;
}

const PROGRESS_MESSAGES = [
  `${BRAND.aiName} liest die Datenpunkte der Anlage…`,
  'Untersysteme werden erkannt (Wärmepumpen, Heizkreise, TWW, …)',
  'Dashboard-Seiten werden entworfen…',
  'Widgets werden positioniert…',
  'Fast fertig — Vorschlag wird zusammengestellt…',
];

export function AiDashboardModal({ plantId, onClose }: Props) {
  const [hint, setHint] = useState('');
  const [stage, setStage] = useState<AiTaskStage>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [proposal, setProposal] = useState<DashboardProposal | null>(null);

  const run = async () => {
    setStage('loading');
    setErrorMsg(null);
    try {
      const res = await api.post<DashboardProposal>(
        `/api/plants/${plantId}/suggest-dashboard`,
        { hint: hint.trim() || null });
      setProposal(res);
      setStage('result');
    } catch (e) {
      setErrorMsg(explain(e));
      setStage('error');
    }
  };

  const apply = async () => {
    if (!proposal) return;
    setStage('applying');
    setErrorMsg(null);
    try {
      // The server-side proposal already contains a fully assembled body_json.
      // Save it as the current user's Personal layout for this plant; we
      // fetch the current version first so the optimistic-concurrency check
      // on SaveLayout sees a matching one.
      let version = 1;
      try {
        const current = await api.get<LayoutDetailResponse>(`/api/layouts/plant/${plantId}`);
        version = current.version;
      } catch (e) {
        // 404 = no personal layout yet → version 1 (server expects that).
        if (!(e instanceof ApiError) || e.status !== 404) throw e;
      }
      await api.put<{ id: string; version: number }>(
        `/api/layouts/plant/${plantId}`,
        { engineKind: 'gridstack', bodyJson: proposal.bodyJson, version });
      onClose();
    } catch (e) {
      setErrorMsg(explain(e));
      setStage('result');
    }
  };

  const totalWidgets = proposal?.pages.reduce((n, p) => n + p.widgets.length, 0) ?? 0;

  return (
    <AiTaskChrome
      title={`${BRAND.aiName} · Dashboard-Seiten vorschlagen`}
      hint={hint}
      onHintChange={setHint}
      hintPlaceholder="z.B.: 'Fokus auf Wärmepumpen', 'eine Seite pro Heizkreis', 'zusätzlich eine Energie-Seite mit Wärmemengen und Zählern'"
      stage={stage}
      errorMessage={errorMsg}
      progressMessages={PROGRESS_MESSAGES}
      onClose={onClose}
      hero={<AiDashboard size={72} style={{ color: 'var(--ai-accent)' }} />}
      chatContext={{ mode: 'dashboard', plantId }}
      footer={
        stage === 'result' ? (
          <>
            <span class="muted" style={{ marginRight: 'auto' }}>
              {proposal?.pages.length ?? 0} Seiten mit insgesamt {totalWidgets} Widgets
            </span>
            <button type="button" class="hv-btn" onClick={onClose}>Abbrechen</button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={apply}
                    disabled={!proposal || proposal.pages.length === 0}>
              Als mein Dashboard speichern
            </button>
          </>
        ) : stage === 'applying' ? (
          <button type="button" class="hv-btn" disabled>Speichere…</button>
        ) : (
          <>
            <div style={{ flex: 1 }} />
            <button type="button" class="hv-btn" onClick={onClose} disabled={stage === 'loading'}>
              Abbrechen
            </button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={run}
                    disabled={stage === 'loading'}>
              {stage === 'loading' ? `${BRAND.aiName} arbeitet…` : 'Vorschlagen'}
            </button>
          </>
        )
      }
    >
      {stage === 'result' && proposal && (
        <>
          {proposal.rationale && <p class="hv-ai-modal__rationale">{proposal.rationale}</p>}
          {proposal.pages.map((page) => (
            <details key={page.title} class="hv-ai-dashboard-page" open>
              <summary class="hv-ai-dashboard-page__title">
                <strong>{page.title}</strong>
                <span class="muted" style={{ marginLeft: '0.5em' }}>· {page.widgets.length} Widgets</span>
              </summary>
              <ul class="hv-ai-dashboard-page__widgets">
                {page.widgets.map((w, i) => (
                  <li key={i} class="hv-ai-dashboard-widget">
                    <span class="hv-ai-dashboard-widget__type">{w.typeId}</span>
                    {w.title && <span class="hv-ai-dashboard-widget__title">{w.title}</span>}
                    {w.leafSymbols.length > 0 && (
                      <span class="hv-ai-dashboard-widget__leaves">
                        {w.leafSymbols.slice(0, 6).join(', ')}
                        {w.leafSymbols.length > 6 && ` … +${w.leafSymbols.length - 6}`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          ))}
          <p class="muted" style={{ fontSize: '0.85em', marginTop: '1em' }}>
            Beim Übernehmen wird dein persönliches Dashboard für diese Anlage überschrieben.
            Das Factory-Default-Layout bleibt unverändert.
          </p>
        </>
      )}
    </AiTaskChrome>
  );
}

function explain(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; detail?: string; title?: string } | undefined;
    return body?.error ?? body?.detail ?? body?.title ?? `Fehler ${e.status}`;
  }
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler.';
}
